import { ref, isRef, Ref } from 'vue'
import { useCommand } from '@cqrs/vue-composables-cqrs'
import * as c from './commands-core'

export function useCreatePortfolioCommandV1(
	strategyWorkspaceId?: number | Ref<number> | (() => number), 
	portfolioWorkspaceId?: number | Ref<number> | (() => number), 
	strategyId?: string | Ref<string> | (() => string), 
	id?: string | Ref<string> | (() => string), 
	name?: string | Ref<string> | (() => string), 
	description?: string | Ref<string> | (() => string), 
) 
{
	const _strategyWorkspaceId = isRef<number>(strategyWorkspaceId) ? strategyWorkspaceId : ref<number>((typeof strategyWorkspaceId === 'function') ? strategyWorkspaceId() : strategyWorkspaceId)
	const _portfolioWorkspaceId = isRef<number>(portfolioWorkspaceId) ? portfolioWorkspaceId : ref<number>((typeof portfolioWorkspaceId === 'function') ? portfolioWorkspaceId() : portfolioWorkspaceId)
	const _strategyId = isRef<string>(strategyId) ? strategyId : ref<string>((typeof strategyId === 'function') ? strategyId() : strategyId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _name = isRef<string>(name) ? name : ref<string>((typeof name === 'function') ? name() : name)
	const _description = isRef<string>(description) ? description : ref<string>((typeof description === 'function') ? description() : description)

	const result = {
		strategyWorkspaceId: _strategyWorkspaceId,
		portfolioWorkspaceId: _portfolioWorkspaceId,
		strategyId: _strategyId,
		id: _id,
		name: _name,
		description: _description,

		...useCommand(() => {
			if (typeof strategyWorkspaceId === 'function') { _strategyWorkspaceId.value = strategyWorkspaceId() }
			if (typeof portfolioWorkspaceId === 'function') { _portfolioWorkspaceId.value = portfolioWorkspaceId() }
			if (typeof strategyId === 'function') { _strategyId.value = strategyId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof name === 'function') { _name.value = name() }
			if (typeof description === 'function') { _description.value = description() }

			return new c.CreatePortfolioCommandV1(_strategyWorkspaceId.value, _portfolioWorkspaceId.value, _strategyId.value, _id.value, _name.value, _description.value)
		})
	}

	return {
		...result,
		authorizedCreatePortfolioCommandV1: result.authorized,
		createPortfolioCommandV1: result.command,
		completedCreatePortfolioCommandV1: result.completed,
		errorCreatePortfolioCommandV1: result.error,
		invokeCreatePortfolioCommandV1: result.invoke,
		invokingCreatePortfolioCommandV1: result.invoking,
		succeedCreatePortfolioCommandV1: result.succeed,
		validationErrorsCreatePortfolioCommandV1: result.validationErrors,
		onSucceedCreatePortfolioCommandV1: result.onSucceed,
	}
}

export function useDeletePortfolioCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)

	const result = {
		workspaceId: _workspaceId,
		id: _id,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }

			return new c.DeletePortfolioCommandV1(_workspaceId.value, _id.value)
		})
	}

	return {
		...result,
		authorizedDeletePortfolioCommandV1: result.authorized,
		deletePortfolioCommandV1: result.command,
		completedDeletePortfolioCommandV1: result.completed,
		errorDeletePortfolioCommandV1: result.error,
		invokeDeletePortfolioCommandV1: result.invoke,
		invokingDeletePortfolioCommandV1: result.invoking,
		succeedDeletePortfolioCommandV1: result.succeed,
		validationErrorsDeletePortfolioCommandV1: result.validationErrors,
		onSucceedDeletePortfolioCommandV1: result.onSucceed,
	}
}

export function useExportAllActiveProjectPlansCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)

	const result = {
		workspaceId: _workspaceId,
		id: _id,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }

			return new c.ExportAllActiveProjectPlansCommandV1(_workspaceId.value, _id.value)
		})
	}

	return {
		...result,
		authorizedExportAllActiveProjectPlansCommandV1: result.authorized,
		exportAllActiveProjectPlansCommandV1: result.command,
		completedExportAllActiveProjectPlansCommandV1: result.completed,
		errorExportAllActiveProjectPlansCommandV1: result.error,
		invokeExportAllActiveProjectPlansCommandV1: result.invoke,
		invokingExportAllActiveProjectPlansCommandV1: result.invoking,
		succeedExportAllActiveProjectPlansCommandV1: result.succeed,
		validationErrorsExportAllActiveProjectPlansCommandV1: result.validationErrors,
		onSucceedExportAllActiveProjectPlansCommandV1: result.onSucceed,
	}
}

export function useImportProjectPlansFromSpreadsheetCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
	blobId?: string | Ref<string> | (() => string), 
	importedPlanName?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _blobId = isRef<string>(blobId) ? blobId : ref<string>((typeof blobId === 'function') ? blobId() : blobId)
	const _importedPlanName = isRef<string>(importedPlanName) ? importedPlanName : ref<string>((typeof importedPlanName === 'function') ? importedPlanName() : importedPlanName)

	const result = {
		workspaceId: _workspaceId,
		id: _id,
		blobId: _blobId,
		importedPlanName: _importedPlanName,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof blobId === 'function') { _blobId.value = blobId() }
			if (typeof importedPlanName === 'function') { _importedPlanName.value = importedPlanName() }

			return new c.ImportProjectPlansFromSpreadsheetCommandV1(_workspaceId.value, _id.value, _blobId.value, _importedPlanName.value)
		})
	}

	return {
		...result,
		authorizedImportProjectPlansFromSpreadsheetCommandV1: result.authorized,
		importProjectPlansFromSpreadsheetCommandV1: result.command,
		completedImportProjectPlansFromSpreadsheetCommandV1: result.completed,
		errorImportProjectPlansFromSpreadsheetCommandV1: result.error,
		invokeImportProjectPlansFromSpreadsheetCommandV1: result.invoke,
		invokingImportProjectPlansFromSpreadsheetCommandV1: result.invoking,
		succeedImportProjectPlansFromSpreadsheetCommandV1: result.succeed,
		validationErrorsImportProjectPlansFromSpreadsheetCommandV1: result.validationErrors,
		onSucceedImportProjectPlansFromSpreadsheetCommandV1: result.onSucceed,
	}
}

export function useLinkPortfolioCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	strategyId?: string | Ref<string> | (() => string), 
	id?: string | Ref<string> | (() => string), 
	portfolioWorkspaceId?: number | Ref<number> | (() => number), 
	portfolioTchProjectId?: number | Ref<number> | (() => number), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _strategyId = isRef<string>(strategyId) ? strategyId : ref<string>((typeof strategyId === 'function') ? strategyId() : strategyId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _portfolioWorkspaceId = isRef<number>(portfolioWorkspaceId) ? portfolioWorkspaceId : ref<number>((typeof portfolioWorkspaceId === 'function') ? portfolioWorkspaceId() : portfolioWorkspaceId)
	const _portfolioTchProjectId = isRef<number>(portfolioTchProjectId) ? portfolioTchProjectId : ref<number>((typeof portfolioTchProjectId === 'function') ? portfolioTchProjectId() : portfolioTchProjectId)

	const result = {
		workspaceId: _workspaceId,
		strategyId: _strategyId,
		id: _id,
		portfolioWorkspaceId: _portfolioWorkspaceId,
		portfolioTchProjectId: _portfolioTchProjectId,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof strategyId === 'function') { _strategyId.value = strategyId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof portfolioWorkspaceId === 'function') { _portfolioWorkspaceId.value = portfolioWorkspaceId() }
			if (typeof portfolioTchProjectId === 'function') { _portfolioTchProjectId.value = portfolioTchProjectId() }

			return new c.LinkPortfolioCommandV1(_workspaceId.value, _strategyId.value, _id.value, _portfolioWorkspaceId.value, _portfolioTchProjectId.value)
		})
	}

	return {
		...result,
		authorizedLinkPortfolioCommandV1: result.authorized,
		linkPortfolioCommandV1: result.command,
		completedLinkPortfolioCommandV1: result.completed,
		errorLinkPortfolioCommandV1: result.error,
		invokeLinkPortfolioCommandV1: result.invoke,
		invokingLinkPortfolioCommandV1: result.invoking,
		succeedLinkPortfolioCommandV1: result.succeed,
		validationErrorsLinkPortfolioCommandV1: result.validationErrors,
		onSucceedLinkPortfolioCommandV1: result.onSucceed,
	}
}

export function useSaveViewAllProjectsPageCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
	grid?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _grid = isRef<string>(grid) ? grid : ref<string>((typeof grid === 'function') ? grid() : grid)

	const result = {
		workspaceId: _workspaceId,
		id: _id,
		grid: _grid,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof grid === 'function') { _grid.value = grid() }

			return new c.SaveViewAllProjectsPageCommandV1(_workspaceId.value, _id.value, _grid.value)
		})
	}

	return {
		...result,
		authorizedSaveViewAllProjectsPageCommandV1: result.authorized,
		saveViewAllProjectsPageCommandV1: result.command,
		completedSaveViewAllProjectsPageCommandV1: result.completed,
		errorSaveViewAllProjectsPageCommandV1: result.error,
		invokeSaveViewAllProjectsPageCommandV1: result.invoke,
		invokingSaveViewAllProjectsPageCommandV1: result.invoking,
		succeedSaveViewAllProjectsPageCommandV1: result.succeed,
		validationErrorsSaveViewAllProjectsPageCommandV1: result.validationErrors,
		onSucceedSaveViewAllProjectsPageCommandV1: result.onSucceed,
	}
}

export function useSaveViewFinishedProjectsPageCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
	grid?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _grid = isRef<string>(grid) ? grid : ref<string>((typeof grid === 'function') ? grid() : grid)

	const result = {
		workspaceId: _workspaceId,
		id: _id,
		grid: _grid,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof grid === 'function') { _grid.value = grid() }

			return new c.SaveViewFinishedProjectsPageCommandV1(_workspaceId.value, _id.value, _grid.value)
		})
	}

	return {
		...result,
		authorizedSaveViewFinishedProjectsPageCommandV1: result.authorized,
		saveViewFinishedProjectsPageCommandV1: result.command,
		completedSaveViewFinishedProjectsPageCommandV1: result.completed,
		errorSaveViewFinishedProjectsPageCommandV1: result.error,
		invokeSaveViewFinishedProjectsPageCommandV1: result.invoke,
		invokingSaveViewFinishedProjectsPageCommandV1: result.invoking,
		succeedSaveViewFinishedProjectsPageCommandV1: result.succeed,
		validationErrorsSaveViewFinishedProjectsPageCommandV1: result.validationErrors,
		onSucceedSaveViewFinishedProjectsPageCommandV1: result.onSucceed,
	}
}

export function useSaveViewOwnerProjectsPageCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
	grid?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _grid = isRef<string>(grid) ? grid : ref<string>((typeof grid === 'function') ? grid() : grid)

	const result = {
		workspaceId: _workspaceId,
		id: _id,
		grid: _grid,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof grid === 'function') { _grid.value = grid() }

			return new c.SaveViewOwnerProjectsPageCommandV1(_workspaceId.value, _id.value, _grid.value)
		})
	}

	return {
		...result,
		authorizedSaveViewOwnerProjectsPageCommandV1: result.authorized,
		saveViewOwnerProjectsPageCommandV1: result.command,
		completedSaveViewOwnerProjectsPageCommandV1: result.completed,
		errorSaveViewOwnerProjectsPageCommandV1: result.error,
		invokeSaveViewOwnerProjectsPageCommandV1: result.invoke,
		invokingSaveViewOwnerProjectsPageCommandV1: result.invoking,
		succeedSaveViewOwnerProjectsPageCommandV1: result.succeed,
		validationErrorsSaveViewOwnerProjectsPageCommandV1: result.validationErrors,
		onSucceedSaveViewOwnerProjectsPageCommandV1: result.onSucceed,
	}
}

export function useSaveViewProposedProjectsPageCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
	grid?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _grid = isRef<string>(grid) ? grid : ref<string>((typeof grid === 'function') ? grid() : grid)

	const result = {
		workspaceId: _workspaceId,
		id: _id,
		grid: _grid,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof grid === 'function') { _grid.value = grid() }

			return new c.SaveViewProposedProjectsPageCommandV1(_workspaceId.value, _id.value, _grid.value)
		})
	}

	return {
		...result,
		authorizedSaveViewProposedProjectsPageCommandV1: result.authorized,
		saveViewProposedProjectsPageCommandV1: result.command,
		completedSaveViewProposedProjectsPageCommandV1: result.completed,
		errorSaveViewProposedProjectsPageCommandV1: result.error,
		invokeSaveViewProposedProjectsPageCommandV1: result.invoke,
		invokingSaveViewProposedProjectsPageCommandV1: result.invoking,
		succeedSaveViewProposedProjectsPageCommandV1: result.succeed,
		validationErrorsSaveViewProposedProjectsPageCommandV1: result.validationErrors,
		onSucceedSaveViewProposedProjectsPageCommandV1: result.onSucceed,
	}
}

export function useSaveViewScheduledProjectsPageCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
	grid?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _grid = isRef<string>(grid) ? grid : ref<string>((typeof grid === 'function') ? grid() : grid)

	const result = {
		workspaceId: _workspaceId,
		id: _id,
		grid: _grid,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof grid === 'function') { _grid.value = grid() }

			return new c.SaveViewScheduledProjectsPageCommandV1(_workspaceId.value, _id.value, _grid.value)
		})
	}

	return {
		...result,
		authorizedSaveViewScheduledProjectsPageCommandV1: result.authorized,
		saveViewScheduledProjectsPageCommandV1: result.command,
		completedSaveViewScheduledProjectsPageCommandV1: result.completed,
		errorSaveViewScheduledProjectsPageCommandV1: result.error,
		invokeSaveViewScheduledProjectsPageCommandV1: result.invoke,
		invokingSaveViewScheduledProjectsPageCommandV1: result.invoking,
		succeedSaveViewScheduledProjectsPageCommandV1: result.succeed,
		validationErrorsSaveViewScheduledProjectsPageCommandV1: result.validationErrors,
		onSucceedSaveViewScheduledProjectsPageCommandV1: result.onSucceed,
	}
}

export function useSaveViewStartedProjectsPageCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
	grid?: string | Ref<string> | (() => string), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _grid = isRef<string>(grid) ? grid : ref<string>((typeof grid === 'function') ? grid() : grid)

	const result = {
		workspaceId: _workspaceId,
		id: _id,
		grid: _grid,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof grid === 'function') { _grid.value = grid() }

			return new c.SaveViewStartedProjectsPageCommandV1(_workspaceId.value, _id.value, _grid.value)
		})
	}

	return {
		...result,
		authorizedSaveViewStartedProjectsPageCommandV1: result.authorized,
		saveViewStartedProjectsPageCommandV1: result.command,
		completedSaveViewStartedProjectsPageCommandV1: result.completed,
		errorSaveViewStartedProjectsPageCommandV1: result.error,
		invokeSaveViewStartedProjectsPageCommandV1: result.invoke,
		invokingSaveViewStartedProjectsPageCommandV1: result.invoking,
		succeedSaveViewStartedProjectsPageCommandV1: result.succeed,
		validationErrorsSaveViewStartedProjectsPageCommandV1: result.validationErrors,
		onSucceedSaveViewStartedProjectsPageCommandV1: result.onSucceed,
	}
}

export function useUpdatePortfolioSettingsCommandV1(
	workspaceId?: number | Ref<number> | (() => number), 
	id?: string | Ref<string> | (() => string), 
	name?: string | Ref<string> | (() => string), 
	description?: string | Ref<string> | (() => string), 
	benefitsConfidenceDate?: any | null | Ref<any | null> | (() => any | null), 
	excludedResourceIds?: any | Ref<any> | (() => any), 
	excludedRevenueIds?: any | Ref<any> | (() => any), 
	excludedExpenseIds?: any | Ref<any> | (() => any), 
	excludedCapexIds?: any | Ref<any> | (() => any), 
) 
{
	const _workspaceId = isRef<number>(workspaceId) ? workspaceId : ref<number>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _name = isRef<string>(name) ? name : ref<string>((typeof name === 'function') ? name() : name)
	const _description = isRef<string>(description) ? description : ref<string>((typeof description === 'function') ? description() : description)
	const _benefitsConfidenceDate = isRef<any | null>(benefitsConfidenceDate) ? benefitsConfidenceDate : ref<any | null>((typeof benefitsConfidenceDate === 'function') ? benefitsConfidenceDate() : benefitsConfidenceDate)
	const _excludedResourceIds = isRef<any>(excludedResourceIds) ? excludedResourceIds : ref<any>((typeof excludedResourceIds === 'function') ? excludedResourceIds() : excludedResourceIds)
	const _excludedRevenueIds = isRef<any>(excludedRevenueIds) ? excludedRevenueIds : ref<any>((typeof excludedRevenueIds === 'function') ? excludedRevenueIds() : excludedRevenueIds)
	const _excludedExpenseIds = isRef<any>(excludedExpenseIds) ? excludedExpenseIds : ref<any>((typeof excludedExpenseIds === 'function') ? excludedExpenseIds() : excludedExpenseIds)
	const _excludedCapexIds = isRef<any>(excludedCapexIds) ? excludedCapexIds : ref<any>((typeof excludedCapexIds === 'function') ? excludedCapexIds() : excludedCapexIds)

	const result = {
		workspaceId: _workspaceId,
		id: _id,
		name: _name,
		description: _description,
		benefitsConfidenceDate: _benefitsConfidenceDate,
		excludedResourceIds: _excludedResourceIds,
		excludedRevenueIds: _excludedRevenueIds,
		excludedExpenseIds: _excludedExpenseIds,
		excludedCapexIds: _excludedCapexIds,

		...useCommand(() => {
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof id === 'function') { _id.value = id() }
			if (typeof name === 'function') { _name.value = name() }
			if (typeof description === 'function') { _description.value = description() }
			if (typeof benefitsConfidenceDate === 'function') { _benefitsConfidenceDate.value = benefitsConfidenceDate() }
			if (typeof excludedResourceIds === 'function') { _excludedResourceIds.value = excludedResourceIds() }
			if (typeof excludedRevenueIds === 'function') { _excludedRevenueIds.value = excludedRevenueIds() }
			if (typeof excludedExpenseIds === 'function') { _excludedExpenseIds.value = excludedExpenseIds() }
			if (typeof excludedCapexIds === 'function') { _excludedCapexIds.value = excludedCapexIds() }

			return new c.UpdatePortfolioSettingsCommandV1(_workspaceId.value, _id.value, _name.value, _description.value, _benefitsConfidenceDate.value, _excludedResourceIds.value, _excludedRevenueIds.value, _excludedExpenseIds.value, _excludedCapexIds.value)
		})
	}

	return {
		...result,
		authorizedUpdatePortfolioSettingsCommandV1: result.authorized,
		updatePortfolioSettingsCommandV1: result.command,
		completedUpdatePortfolioSettingsCommandV1: result.completed,
		errorUpdatePortfolioSettingsCommandV1: result.error,
		invokeUpdatePortfolioSettingsCommandV1: result.invoke,
		invokingUpdatePortfolioSettingsCommandV1: result.invoking,
		succeedUpdatePortfolioSettingsCommandV1: result.succeed,
		validationErrorsUpdatePortfolioSettingsCommandV1: result.validationErrors,
		onSucceedUpdatePortfolioSettingsCommandV1: result.onSucceed,
	}
}

